/**
 * Library dropdown — collapses the four library-drawer launchers
 * (Formations / Patterns / Team tactics / Set pieces) into one
 * chip + popover. Reduces Design-row visual noise without losing
 * any of the underlying drawers.
 *
 * Each menu item just fires the existing setter that opened the
 * respective drawer — the drawers themselves are unchanged.
 *
 * The popover is rendered via React Portal directly into
 * `document.body` and positioned relative to the chip's bounding
 * rect. This sidesteps the HUD row's `backdrop-filter` stacking
 * context that would otherwise trap an in-flow `position: absolute`
 * popover below the row's painted layer.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { HudChip } from './HudChip';
import './LibraryMenu.css';

export interface LibraryMenuProps {
  /** Highlight the chip when any of the library drawers is open. */
  anyOpen: boolean;
  onOpenPatterns: () => void;
  onOpenTeamTactics: () => void;
  /** Set pieces moved to /setpieces as its own sibling surface (2026-05-19).
   *  This callback now just navigates there — kept on the props for parent
   *  ownership of the router call. */
  onOpenSetPieces: () => void;
}

export function LibraryMenu({
  anyOpen,
  onOpenPatterns,
  onOpenTeamTactics,
  onOpenSetPieces,
}: LibraryMenuProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; bottom: number } | null>(null);

  // Reposition the popover above the chip whenever it opens or the
  // chip moves (window resize / HUD reflow). useLayoutEffect avoids
  // a one-frame flash where the popover renders at 0,0 before
  // positioning.
  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      setPos({
        left: rect.left,
        // Anchor the popover's bottom 6px above the chip's top edge.
        // window.innerHeight - rect.top because `bottom` in CSS is
        // measured from the viewport's bottom.
        bottom: window.innerHeight - rect.top + 6,
      });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointer = (e: PointerEvent) => {
      const trigger = triggerRef.current;
      const pop = popoverRef.current;
      if (e.target instanceof Node) {
        if (trigger?.contains(e.target)) return;
        if (pop?.contains(e.target)) return;
      }
      setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const t = setTimeout(() => {
      window.addEventListener('pointerdown', onPointer);
    }, 0);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer);
      clearTimeout(t);
    };
  }, [open]);

  // Formations live on the Home/Away rows now (FormationDropdown);
  // not duplicated here. Set pieces moved to /setpieces (sibling surface)
  // — the Set pieces menu item navigates rather than opening a drawer.
  const items: Array<{ label: string; meta: string; onClick: () => void }> = [
    { label: 'Patterns',    meta: 'famous + your saved',           onClick: onOpenPatterns },
    { label: 'Team tactics', meta: 'sliders, build-up, press',     onClick: onOpenTeamTactics },
    { label: 'Set pieces',  meta: 'open /setpieces →',              onClick: onOpenSetPieces },
  ];

  return (
    <div className="library-menu" ref={triggerRef} data-testid="library-menu">
      <HudChip
        active={anyOpen || open}
        ariaLabel="Open the library menu — formations, patterns, team tactics, set pieces"
        onClick={() => setOpen((o) => !o)}
      >
        Library ▾
      </HudChip>
      {open && pos && typeof document !== 'undefined' && createPortal(
        <div
          ref={popoverRef}
          className="library-menu__popover"
          role="menu"
          aria-label="Library"
          data-testid="library-menu-popover"
          style={{ left: pos.left, bottom: pos.bottom }}
        >
          {items.map(({ label, meta, onClick }) => (
            <button
              key={label}
              type="button"
              role="menuitem"
              className="library-menu__item"
              onClick={() => {
                setOpen(false);
                onClick();
              }}
            >
              <span className="library-menu__item-label">{label}</span>
              <span className="library-menu__item-meta">{meta}</span>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
